import React, { ChangeEvent, FocusEvent, FC, useId } from 'react'
import Dropdown from '../Dropdown/Dropdown.component'
import './InputField.css'

type InputFieldProps = {
  label?: React.ReactNode
  placeholder?: string
  value?: string
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  leftIcon?: React.ReactNode
  leftText?: string
  leftDropdownText?: string
  rightIcon?: React.ReactNode
  rightText?: string
  hint?: string
  destructive?: boolean
  leftOptions?: string[]
  rightOptions?: string[]
  onLeftDropdownChange?: (selectedOption: string) => void
  onRightDropdownChange?: (selectedOption: string) => void
  onLeftIconClick?: () => void
  onRightIconClick?: () => void
  type?: string
  className?: string
  handleKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  max?: string
  min?: string
  ref?: React.RefObject<HTMLInputElement>
  isControlled?: boolean
  uncontrolledDefaultValue?: string
  required?: boolean
  includeOptionalLabel?: boolean
  ariaLabel?: string
}

const generateInputClassName = ({
  leftIcon,
  leftText,
  leftDropdown,
  rightIcon,
  rightText,
  rightDropdown,
  disabled,
  destructive,
}: {
  leftIcon?: React.ReactNode
  leftText?: string
  leftDropdown?: string[]
  rightIcon?: React.ReactNode
  rightText?: string
  rightDropdown?: string[]
  disabled?: boolean
  destructive?: boolean
}): string =>
  [
    'input-field',
    leftIcon && 'input-field-with-left-icon',
    leftText && 'input-field-with-left-text',
    leftDropdown?.length && 'input-field-with-left-dropdown',
    rightIcon && 'input-field-with-right-icon',
    (rightText || rightDropdown?.length) && 'input-field-with-right-element',
    disabled && 'input-field-disabled',
    destructive && 'input-field-destructive',
    leftText === 'http://' && 'input-field-leading-text',
  ]
    .filter(Boolean)
    .join(' ')

const InputField: FC<InputFieldProps> = ({
  label,
  placeholder = '',
  value = '',
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  leftIcon,
  leftText,
  leftDropdownText,
  rightIcon,
  rightText,
  hint,
  destructive = false,
  leftOptions = [],
  rightOptions = [],
  onLeftDropdownChange,
  onRightDropdownChange,
  onLeftIconClick,
  onRightIconClick,
  type = 'text',
  className,
  handleKeyDown,
  max,
  min,
  ref,
  isControlled = true,
  uncontrolledDefaultValue = '',
  required = false,
  includeOptionalLabel = false,
  ariaLabel,
}) => {
  // Ensure value is always a string to prevent React warnings
  const safeValue = value ?? ''
  const inputId = useId()
  const hintId = `${inputId}-hint`
  const inputClassName = generateInputClassName({
    leftIcon,
    leftText,
    leftDropdown: leftOptions,
    rightIcon,
    rightText,
    rightDropdown: rightOptions,
    disabled,
    destructive,
  })

  return (
    <div className={`input-field-container ${className}`}>
      <div className="d-flex align-items-center">
        {label && (
          <label className="input-field-label" htmlFor={inputId}>
            {label}
          </label>
        )}
        {includeOptionalLabel && (
          <small className="text-optional ms-1">(Optional)</small>
        )}
      </div>
      <div
        className={`input-wrapper ${leftText === 'http://' ? 'input-wrapper-leading-text' : ''}`}
      >
        {leftIcon && (
          <span className="input-left-icon" onClick={onLeftIconClick}>
            {leftIcon}
          </span>
        )}
        {leftText && !leftOptions.length && (
          <div
            className={`input-left-element ${leftText === 'http://' ? 'input-left-text-custom' : ''}`}
          >
            {leftText}
          </div>
        )}
        {leftOptions.length > 0 && (
          <Dropdown<string>
            options={leftOptions}
            selectedOption={leftDropdownText || ''}
            onOptionChange={onLeftDropdownChange || (() => {})}
            renderOption={(option) => <span>{option}</span>}
            renderSelectedOption={(option) => <span>{option}</span>}
          />
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          className={`${inputClassName} ${className}`}
          placeholder={placeholder}
          {...(isControlled
            ? { value: safeValue }
            : { defaultValue: uncontrolledDefaultValue })}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
          aria-disabled={disabled}
          aria-label={ariaLabel || undefined}
          aria-invalid={destructive || undefined}
          aria-required={required || undefined}
          aria-describedby={hint ? hintId : undefined}
          onKeyDown={handleKeyDown}
          max={max}
          min={min}
          required={required}
        />
        {(rightText || rightOptions.length > 0) && (
          <div className="input-right-element">
            {rightOptions.length > 0 ? (
              <Dropdown<string>
                options={rightOptions}
                selectedOption={rightText || ''}
                onOptionChange={onRightDropdownChange || (() => {})}
                renderOption={(option) => <span>{option}</span>}
                renderSelectedOption={(option) => <span>{option}</span>}
              />
            ) : (
              <span className="input-right-text">{rightText}</span>
            )}
          </div>
        )}
        {rightIcon && (
          <span className="input-right-icon" onClick={onRightIconClick}>
            {rightIcon}
          </span>
        )}
      </div>
      {hint && (
        <span
          id={hintId}
          className="hint-text"
          role={destructive ? 'alert' : undefined}
        >
          {hint}
        </span>
      )}
    </div>
  )
}

export default InputField
